/**
 * Oracle Script Provider
 *
 * Implements SQL script generation for Oracle Database.
 * Uses double-quote identifier quoting (same as PostgreSQL).
 * Oracle-specific: FETCH FIRST N ROWS ONLY, NUMBER/VARCHAR2 types,
 * CASCADE CONSTRAINTS PURGE for DROP TABLE.
 */

import type {
  ScriptProvider,
  TableScriptType,
  TableScriptContext,
  DatabaseScriptType,
  DatabaseScriptContext,
  ColumnMetadata,
  NewObjectTemplateContext,
} from '../types/scriptProvider';
import { defaultValuesInsert } from '../../../utils/defaultValuesInsert';
import { SELECT_TOP_ROW_LIMIT } from '../selectTopRowLimit';
import { appendIndexStatements } from './indexStatements';
import { resolvePrimaryKey } from './primaryKeyClause';

function formatColumnDataType(col: ColumnMetadata): string {
  const { dataType, maxLength, precision, scale } = col;
  const upperType = dataType.toUpperCase();

  // Types with length
  if (['VARCHAR2', 'NVARCHAR2'].includes(upperType)) {
    return maxLength ? `${upperType}(${maxLength})` : `${upperType}(255)`;
  }
  if (['CHAR', 'NCHAR'].includes(upperType)) {
    return maxLength ? `${upperType}(${maxLength})` : `${upperType}(1)`;
  }
  if (upperType === 'RAW') {
    return maxLength ? `RAW(${maxLength})` : 'RAW(2000)';
  }

  // NUMBER with precision and scale
  if (upperType === 'NUMBER') {
    if (precision != null && scale != null && scale > 0) {
      return `NUMBER(${precision}, ${scale})`;
    }
    if (precision != null) {
      return `NUMBER(${precision})`;
    }
    return 'NUMBER';
  }
  if (upperType === 'FLOAT') {
    return precision != null ? `FLOAT(${precision})` : 'FLOAT';
  }

  // Timestamp types with precision
  if (upperType.startsWith('TIMESTAMP')) {
    if (precision != null && precision !== 6) {
      if (upperType.includes('WITH LOCAL TIME ZONE')) return `TIMESTAMP(${precision}) WITH LOCAL TIME ZONE`;
      if (upperType.includes('WITH TIME ZONE')) return `TIMESTAMP(${precision}) WITH TIME ZONE`;
      return `TIMESTAMP(${precision})`;
    }
    return upperType;
  }

  // Types that are used as-is
  const passthroughTypes = [
    'DATE', 'CLOB', 'NCLOB', 'BLOB', 'XMLTYPE', 'JSON',
    'BINARY_FLOAT', 'BINARY_DOUBLE', 'ROWID', 'UROWID',
    'LONG', 'LONG RAW', 'BFILE',
    'INTERVAL YEAR TO MONTH', 'INTERVAL DAY TO SECOND',
  ];
  if (passthroughTypes.includes(upperType)) {
    return upperType;
  }

  return upperType;
}

function generateCreateScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `"${schema}"."${tableName}"`;

  if (!metadata?.columns?.length) {
    return `-- CREATE TABLE script for ${fullName}\n-- Table metadata not available\nCREATE TABLE ${fullName} (\n    -- Column definitions would appear here\n);\n`;
  }

  const lines: string[] = [];
  lines.push(`CREATE TABLE ${fullName} (`);

  const columnDefs: string[] = [];

  for (const col of metadata.columns) {
    let colDef = `    "${col.name}" ${formatColumnDataType(col)}`;

    if (col.defaultValue && !col.isIdentity) {
      colDef += ` DEFAULT ${col.defaultValue}`;
    }

    if (!col.isIdentity) {
      colDef += col.isNullable ? ' NULL' : ' NOT NULL';
    }

    if (col.isIdentity) {
      colDef += ' GENERATED ALWAYS AS IDENTITY';
    }

    columnDefs.push(colDef);
  }

  lines.push(columnDefs.join(',\n'));

  // Primary key constraint — the catalog's name (`SYS_C00…` for an unnamed
  // key) and column order when the metadata carries it (see `resolvePrimaryKey`).
  const primaryKey = resolvePrimaryKey(metadata, `PK_${tableName}`);
  if (primaryKey) {
    const pkCols = primaryKey.columns.map(c => `"${c}"`).join(', ');
    lines.push(`,    CONSTRAINT "${primaryKey.name.replace(/"/g, '""')}" PRIMARY KEY (${pkCols})`);
  }

  lines.push(');');

  // The table's indexes follow as CREATE INDEX statements — the Designer's
  // Indexes tab reads them from the buffer (see `indexStatements.ts`). An
  // Oracle index is a schema object of its own, named `"OWNER"."IX"`.
  return appendIndexStatements(lines.join('\n'), metadata, {
    quote: name => `"${name.replace(/"/g, '""')}"`, table: fullName, indexSchema: schema,
  });
}

function generateInsertScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `"${schema}"."${tableName}"`;

  if (!metadata?.columns?.length) {
    return `INSERT INTO ${fullName} (column1, column2, ...)\nVALUES (value1, value2, ...);`;
  }

  const insertColumns = metadata.columns.filter(c => !c.isIdentity);
  // D5 — every column is an identity column. Oracle has no `DEFAULT VALUES`,
  // so name the first column and hand it DEFAULT.
  if (insertColumns.length === 0) {
    return defaultValuesInsert({
      engineId: 'oracle',
      tableName: fullName,
      quotedColumn: `"${metadata.columns[0].name}"`,
    });
  }
  const columnNames = insertColumns.map(c => `"${c.name}"`).join(', ');
  const valuePlaceholders = insertColumns.map(c => {
    const upper = c.dataType.toUpperCase();
    if (['NUMBER', 'BINARY_FLOAT', 'BINARY_DOUBLE', 'FLOAT'].some(t => upper.includes(t))) {
      return '0';
    }
    if (upper.includes('DATE') || upper.includes('TIMESTAMP')) {
      return 'SYSDATE';
    }
    return `''`;
  }).join(', ');

  return `INSERT INTO ${fullName} (${columnNames})\nVALUES (${valuePlaceholders});`;
}

function generateUpdateScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `"${schema}"."${tableName}"`;

  if (!metadata?.columns?.length) {
    return `UPDATE ${fullName}\nSET column1 = value1,\n    column2 = value2\nWHERE <search_condition>;`;
  }

  const pkColumns = metadata.columns.filter(c => c.isPrimaryKey);
  const updateColumns = metadata.columns.filter(c => !c.isPrimaryKey && !c.isIdentity);

  const setClauses = updateColumns.slice(0, 3).map(c => `    "${c.name}" = <value>`).join(',\n');
  const whereClause = pkColumns.length > 0
    ? pkColumns.map(c => `"${c.name}" = <value>`).join(' AND ')
    : '<search_condition>';

  return `UPDATE ${fullName}\nSET\n${setClauses}\nWHERE ${whereClause};`;
}

function generateDeleteScript(ctx: TableScriptContext): string {
  const { schema, tableName, metadata } = ctx;
  const fullName = `"${schema}"."${tableName}"`;

  if (!metadata?.columns?.length) {
    return `DELETE FROM ${fullName}\nWHERE <search_condition>;`;
  }

  const pkColumns = metadata.columns.filter(c => c.isPrimaryKey);
  const whereClause = pkColumns.length > 0
    ? pkColumns.map(c => `"${c.name}" = <value>`).join(' AND ')
    : '<search_condition>';

  return `DELETE FROM ${fullName}\nWHERE ${whereClause};`;
}

export const oracleScriptProvider: ScriptProvider = {
  engine: 'oracle',

  quoteIdentifier(name: string): string {
    return `"${name.replace(/"/g, '""')}"`;
  },

  qualifyTableName(schema: string, table: string): string {
    return `"${schema.replace(/"/g, '""')}"."${table.replace(/"/g, '""')}"`;
  },

  generateTableScript(type: TableScriptType, ctx: TableScriptContext): string {
    const fullName = this.qualifyTableName(ctx.schema, ctx.tableName);

    switch (type) {
      case 'create':
        return generateCreateScript(ctx);

      case 'drop':
        return `DROP TABLE ${fullName} CASCADE CONSTRAINTS PURGE;`;

      case 'select':
        return `SELECT *\nFROM ${fullName};`;

      case 'select-top':
        return `SELECT *\nFROM ${fullName}\nFETCH FIRST ${SELECT_TOP_ROW_LIMIT} ROWS ONLY;`;

      case 'insert':
        return generateInsertScript(ctx);

      case 'update':
        return generateUpdateScript(ctx);

      case 'delete':
        return generateDeleteScript(ctx);

      default:
        throw new Error(`Unknown table script type: ${type}`);
    }
  },

  generateDatabaseScript(type: DatabaseScriptType, ctx: DatabaseScriptContext): string {
    const { databaseName } = ctx;
    const quotedName = `"${databaseName}"`;

    switch (type) {
      case 'create':
        return `-- Oracle: Create a new schema (user)\nCREATE USER ${quotedName} IDENTIFIED BY <password>\n    DEFAULT TABLESPACE USERS\n    TEMPORARY TABLESPACE TEMP\n    QUOTA UNLIMITED ON USERS;\n\nGRANT CONNECT, RESOURCE TO ${quotedName};`;

      case 'drop':
        return `-- Warning: This will permanently delete the schema and all its objects\nDROP USER ${quotedName} CASCADE;`;

      case 'use':
        return `ALTER SESSION SET CURRENT_SCHEMA = ${quotedName};`;

      default:
        throw new Error(`Unknown database script type: ${type}`);
    }
  },

  generateNewTableTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new table in ${database}.${schema}
CREATE TABLE "${schema}"."\${1:table_name}" (
    "\${2:id}" NUMBER GENERATED ALWAYS AS IDENTITY,
    "\${3:column1}" \${4:VARCHAR2(255)},
    "CREATED_AT" TIMESTAMP DEFAULT SYSTIMESTAMP,
    CONSTRAINT "PK_\${1:table_name}" PRIMARY KEY ("\${2:id}")
);\$0`;
  },

  generateNewViewTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new view in ${database}.${schema}
CREATE OR REPLACE VIEW "${schema}"."\${1:view_name}"
AS
SELECT
    \${2:*}
FROM
    "${schema}"."\${3:source_table}"
WHERE
    \${4:1 = 1};\$0`;
  },

  generateNewProcedureTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new stored procedure in ${database}.${schema}
CREATE OR REPLACE PROCEDURE "${schema}"."\${1:procedure_name}" (
    \${2:p_param1} IN \${3:NUMBER},
    \${4:p_param2} IN \${5:VARCHAR2} DEFAULT NULL
)
AS
BEGIN
    \${6:-- Procedure logic here}
    NULL;
END "\${1:procedure_name}";
/\$0`;
  },

  generateNewFunctionTemplate(ctx: NewObjectTemplateContext): string {
    const { database, schema } = ctx;
    return `-- Create new function in ${database}.${schema}
CREATE OR REPLACE FUNCTION "${schema}"."\${1:function_name}" (
    \${2:p_param1} IN \${3:NUMBER}
)
RETURN \${4:NUMBER}
AS
    v_result \${4:NUMBER};
BEGIN
    \${5:-- Function logic here}
    RETURN v_result;
END "\${1:function_name}";
/\$0`;
  },

  generateNewDatabaseTemplate(): string {
    return `-- Create new Oracle schema (user)
CREATE USER "\${1:schema_name}" IDENTIFIED BY "\${2:password}"
    DEFAULT TABLESPACE \${3:USERS}
    TEMPORARY TABLESPACE \${4:TEMP}
    QUOTA UNLIMITED ON \${3:USERS};

GRANT CONNECT, RESOURCE TO "\${1:schema_name}";\$0`;
  },

  generateNewPackageTemplate(ctx: NewObjectTemplateContext): string {
    const { schema } = ctx;
    return `CREATE OR REPLACE PACKAGE "${schema}"."\${1:package_name}" AS
    -- Package specification
    PROCEDURE \${2:procedure_name} (\${3:p_param} IN VARCHAR2);
    FUNCTION \${4:function_name} (\${5:p_param} IN NUMBER) RETURN VARCHAR2;
END "\${1:package_name}";
/

CREATE OR REPLACE PACKAGE BODY "${schema}"."\${1:package_name}" AS
    PROCEDURE \${2:procedure_name} (\${3:p_param} IN VARCHAR2) IS
    BEGIN
        NULL; -- TODO: implement
    END \${2:procedure_name};

    FUNCTION \${4:function_name} (\${5:p_param} IN NUMBER) RETURN VARCHAR2 IS
    BEGIN
        RETURN NULL; -- TODO: implement
    END \${4:function_name};
END "\${1:package_name}";
/\$0`;
  },

  generateNewSequenceTemplate(ctx: NewObjectTemplateContext): string {
    const { schema } = ctx;
    return `CREATE SEQUENCE "${schema}"."\${1:sequence_name}"
    START WITH \${2:1}
    INCREMENT BY \${3:1}
    NOCACHE
    NOCYCLE;\$0`;
  },

  generateNewSynonymTemplate(ctx: NewObjectTemplateContext): string {
    const { schema } = ctx;
    return `CREATE OR REPLACE SYNONYM "${schema}"."\${1:synonym_name}"
    FOR "\${2:target_schema}"."\${3:target_object}";\$0`;
  },

  generateNewTriggerTemplate(ctx: NewObjectTemplateContext): string {
    const { schema } = ctx;
    return `CREATE OR REPLACE TRIGGER "${schema}"."\${1:trigger_name}"
    BEFORE \${2:INSERT} ON "${schema}"."\${3:table_name}"
    FOR EACH ROW
BEGIN
    \${4:NULL; -- TODO: implement}
END "\${1:trigger_name}";
/\$0`;
  },
};
